import { ReactNode } from 'react';
import { cn } from 'shared/utils/cn';

export type QuoteSize = 'sm' | 'md' | 'lg';
export type QuoteVariant = 'plain' | 'card';

export interface QuoteProps {
  variant?: QuoteVariant;
  size?: QuoteSize;
  attribution?: ReactNode;
  className?: string;
  children: ReactNode;
}

const sizeClasses: Record<QuoteSize, string> = {
  sm: 'text-lg',
  md: 'text-2xl',
  lg: 'text-3xl',
};

export const Quote = ({
  variant = 'plain',
  size = 'md',
  attribution,
  className,
  children,
}: QuoteProps) => {
  const isCard = variant === 'card';

  const figureClasses = cn(
    'not-italic',
    isCard && 'bg-surface-container-lowest p-12 rounded-2xl relative border-l-4 border-tertiary/20',
    className,
  );

  const blockquoteClasses = cn(
    'font-serif italic text-on-surface leading-snug',
    sizeClasses[size],
    isCard && 'relative z-10',
  );

  const captionClasses = cn(
    'mt-4',
    isCard
      ? 'font-sans text-xs uppercase tracking-widest text-tertiary font-bold relative z-10'
      : 'font-sans text-sm text-on-surface-variant',
  );

  return (
    <figure className={figureClasses}>
      {isCard ? (
        <span
          className="font-serif text-6xl text-tertiary/20 absolute top-4 left-6"
          aria-hidden="true"
        >
          &ldquo;
        </span>
      ) : null}
      <blockquote className={blockquoteClasses}>{children}</blockquote>
      {attribution ? (
        <figcaption className={captionClasses}>
          <cite className="not-italic">{attribution}</cite>
        </figcaption>
      ) : null}
      {isCard ? (
        <span
          className="font-serif text-6xl text-tertiary/20 absolute -bottom-2 right-6"
          aria-hidden="true"
        >
          &rdquo;
        </span>
      ) : null}
    </figure>
  );
};
